import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

// Step order is the source of truth for navigation. `currentStep` is a 1-based
// POSITION into this array; StepContainer's STEP_MAP maps each position to the
// matching component. Each step component looks up its own config by `id` (not
// by index) so this array can be reordered freely. `group` drives the StepRail
// phase headers.
export const STEPS = [
  // ─── SETUP ───
  { id: 'details',      label: 'Practice Details', group: 'SETUP',      title: 'Tell us about your practice', subtitle: 'We use this to set up your profile — it shows up in patient-facing materials.', isPrimeIv: false },
  { id: 'locations',    label: 'Locations',        group: 'SETUP',      title: 'Where do you see patients?',  subtitle: 'Every address where your providers will be operating. We ship kits here too.', isPrimeIv: false },
  { id: 'owners',       label: 'Owners',           group: 'SETUP',      title: 'Who owns this practice?',     subtitle: 'Required for agreements. Anyone with ownership interest in the practice.', isPrimeIv: false },

  // ─── OPERATIONS ───
  // Network+ now leads OPERATIONS: the multi-state-coverage decision determines
  // whether the practice needs its own providers, so it must come BEFORE the
  // Providers step (whose isNetworkPlus conditional reads this answer).
  { id: 'networkplus',  label: 'Network+',         group: 'OPERATIONS', title: 'Multi-state coverage',        subtitle: "Network+ lets your providers prescribe in states where they're not licensed. One-time setup.", isPrimeIv: false },
  { id: 'providers',    label: 'Providers',        group: 'OPERATIONS', title: 'Who will be prescribing?',    subtitle: 'Every licensed provider who will write prescriptions through BRITE.', isPrimeIv: false },
  { id: 'kits',         label: 'Kits',             group: 'OPERATIONS', title: 'Starter kits',                subtitle: 'A demo kit for each provider — so they can see and feel the product before prescribing.', isPrimeIv: false },
  { id: 'teams',        label: 'Teams',            group: 'OPERATIONS', title: 'Who else needs access?',      subtitle: "Your team gets their own login to the Operations Hub — your practice's staff portal.", isPrimeIv: false },

  // ─── BILLING ───
  { id: 'billing',      label: 'Payment & Labs',   group: 'BILLING',    title: 'How billing works',           subtitle: 'Set who pays for what — hormones, testing, and shipping. These settings apply to all future orders.', isPrimeIv: false },
  { id: 'managedBilling', label: 'Managed Billing', group: 'BILLING',   title: 'Your patient billing, handled', subtitle: 'BRITE collects from your patients and deposits to you monthly.', isPrimeIv: false },

  // ─── FINISH ───
  { id: 'review',       label: 'Review',           group: 'FINISH',     title: 'One last look',               subtitle: "Everything you've entered, in one place. Edit anything before you submit.", isPrimeIv: false },
  // NOTE: 'agreements' (Step 11) is intentionally removed from the flow — Review
  // is now the terminal step and submits to /submit without signatures. The
  // Step11Agreements component + StepContainer mapping stay on disk so the
  // signing flow can be reinstated later as a separate step.
]

const TOTAL = STEPS.length

// 1-based position of the Kits step. Kits ALWAYS ships now — even under
// Network+ with no own providers, the demo kit defaults to the practice owner
// (see Step8Kits owner fallback), so the step is never auto-skipped.
const KITS_POS = STEPS.findIndex((s) => s.id === 'kits') + 1

function shouldSkipKits() {
  return false
}

// The pre-reorder step order (1-based). Used by the persist migration to remap
// a stale `currentStep`/`history` position (saved under the old order) to its
// new position by step id — formData is keyed by id so it's already safe.
const V1_ORDER = [
  'details', 'locations', 'owners', 'providers', 'kits', 'teams',
  'billing', 'managedBilling', 'networkplus', 'review',
]

function remapV1Position(pos) {
  if (typeof pos !== 'number') return pos
  const id = V1_ORDER[pos - 1]
  if (!id) return pos
  const idx = STEPS.findIndex((s) => s.id === id)
  return idx >= 0 ? idx + 1 : pos
}

export const useOnboardingStore = create(
  persist(
    (set, get) => ({
      currentStep: 1,
      steps: STEPS,
      formData: {},
      history: [],
      hasStartedFlow: false,
      // Session-scoped — not persisted. Welcome is always the first surface
      // per session. Flips true only on explicit Begin/Resume click.
      hasEntered: false,
      savedAt: null,
      isComplete: false,
      submissionId: null,
      completedAt: null,
      // CAP-pending state — set when owner submits but CAP still needs to sign.
      // Persisted so the success page renders the right variant after refresh.
      awaitingCap: false,
      capName: null,
      capEmail: null,
      // Personalization payload from /onboarding/personalization?slug=<p>.
      // Set once on welcome page mount. Read by WelcomeCard for greetings and
      // by Step5NetworkPlus to auto-apply the coupon.
      personalization: null,
      // Lite mode flag — true when the user lands on /docther. Strips Step 11
      // (no contracts/signatures), forces DTC practice type, hides N+ skip,
      // and routes Review submit to /submit-lite. One-off for Kashif Friday.
      liteMode: false,

      markSaved: () => set({ savedAt: Date.now() }),
      setHasEntered: (v) => set({ hasEntered: !!v }),
      setPersonalization: (p) => set({ personalization: p || null }),
      setLiteMode: (v) => set({ liteMode: !!v }),

      markComplete: (submissionId, opts = {}) =>
        set({
          isComplete: true,
          submissionId: submissionId || null,
          completedAt: Date.now(),
          awaitingCap: opts.awaitingCap === true,
          capName: opts.capName || null,
          capEmail: opts.capEmail || null,
        }),

      startFlow: () =>
        set({
          hasStartedFlow: true,
          hasEntered: true,
          currentStep: 1,
          history: [1],
        }),

      resumeFlow: () =>
        set((s) => ({
          hasStartedFlow: true,
          hasEntered: true,
          currentStep: s.currentStep > 1 ? s.currentStep : 1,
        })),

      goToStep: (stepNumber) => {
        if (stepNumber < 1 || stepNumber > TOTAL) return
        set((s) => ({
          currentStep: stepNumber,
          history: [...s.history, stepNumber],
        }))
      },

      goToStepId: (stepId) => {
        const idx = STEPS.findIndex((s) => s.id === stepId)
        if (idx === -1) return
        const num = idx + 1
        set((s) => ({
          currentStep: num,
          history: [...s.history, num],
        }))
      },

      nextStep: () => {
        const { currentStep, formData } = get()
        if (currentStep >= TOTAL) return
        let next = currentStep + 1
        // Skip Kits when N+ is on and there are no own providers to kit.
        if (next === KITS_POS && shouldSkipKits(formData)) {
          next = Math.min(next + 1, TOTAL)
        }
        set((s) => ({
          currentStep: next,
          history: [...s.history, next],
        }))
      },

      prevStep: () => {
        const { currentStep, formData } = get()
        if (currentStep <= 1) return
        let prev = currentStep - 1
        // Mirror the forward skip so Back from Teams lands on Providers.
        if (prev === KITS_POS && shouldSkipKits(formData)) {
          prev = Math.max(prev - 1, 1)
        }
        set((s) => ({
          currentStep: prev,
          history: [...s.history, prev],
        }))
      },

      updateFormData: (stepId, data) =>
        set((s) => ({
          formData: { ...s.formData, [stepId]: { ...s.formData[stepId], ...data } },
          savedAt: Date.now(),
        })),

      setNetworkPlusCoupon: (promo) =>
        set((s) => {
          const stripeSlice = s.formData.stripe || {}
          const nplus = stripeSlice.networkPlus || {}
          return {
            formData: {
              ...s.formData,
              stripe: {
                ...stripeSlice,
                networkPlus: { ...nplus, appliedPromotionCode: promo },
              },
            },
            savedAt: Date.now(),
          }
        }),

      clearNetworkPlusCoupon: () =>
        set((s) => {
          const stripeSlice = s.formData.stripe || {}
          const nplus = stripeSlice.networkPlus || {}
          return {
            formData: {
              ...s.formData,
              stripe: {
                ...stripeSlice,
                networkPlus: { ...nplus, appliedPromotionCode: null },
              },
            },
            savedAt: Date.now(),
          }
        }),

      setNetworkPlusPayment: ({ invoiceId, paymentIntentId }) =>
        set((s) => {
          const stripeSlice = s.formData.stripe || {}
          const nplus = stripeSlice.networkPlus || {}
          return {
            formData: {
              ...s.formData,
              stripe: {
                ...stripeSlice,
                networkPlus: {
                  ...nplus,
                  invoiceId: invoiceId || null,
                  paymentIntentId: paymentIntentId || null,
                  paidAt: new Date().toISOString(),
                },
              },
            },
            savedAt: Date.now(),
          }
        }),

      setNetworkPlusAppliedPromotionCode: (promo) =>
        set((state) => ({
          formData: {
            ...state.formData,
            stripe: {
              ...(state.formData.stripe || {}),
              networkPlus: {
                ...((state.formData.stripe && state.formData.stripe.networkPlus) || {}),
                appliedPromotionCode: promo,
              },
            },
          },
        })),

      getAllEmails: () => {
        const fd = get().formData || {}
        const norm = (list) =>
          (list || [])
            .map((x) => String(x?.email ?? '').trim().toLowerCase())
            .filter(Boolean)
        return {
          owners: norm(fd.owners?.owners),
          providers: norm(fd.providers?.providers),
          teams: norm(fd.teams?.teams),
        }
      },

      reset: () =>
        set({
          currentStep: 1,
          formData: {},
          history: [],
          hasStartedFlow: false,
          hasEntered: false,
          savedAt: null,
          isComplete: false,
          submissionId: null,
          completedAt: null,
          awaitingCap: false,
          capName: null,
          capEmail: null,
        }),
    }),
    {
      name: 'brite-onboarding-state-v1',
      storage: createJSONStorage(() => localStorage),
      // v2: steps were reordered (Network+ moved before Providers). Old sessions
      // stored a position-based currentStep/history under the old order — remap
      // them by step id so a mid-flow resume lands on the right page.
      version: 2,
      migrate: (state, version) => {
        if (state && version < 2) {
          state.currentStep = remapV1Position(state.currentStep)
          if (Array.isArray(state.history)) {
            state.history = state.history.map(remapV1Position)
          }
        }
        return state
      },
      partialize: (s) => {
        // Only persist logo when it's a brandfetch URL reference.
        // Upload-source logos hold a File + dataUrl which can't (shouldn't)
        // round-trip through localStorage — user re-uploads on resume.
        let persistedLogo
        const logo = s.formData?.details?.logo
        if (
          logo &&
          (logo.source === 'brandfetch' || logo.source === 'favicon') &&
          logo.url
        ) {
          persistedLogo = { source: logo.source, url: logo.url }
        } else {
          persistedLogo = null
        }
        return {
          currentStep: s.currentStep,
          formData: {
            ...s.formData,
            details: s.formData.details
              ? { ...s.formData.details, logo: persistedLogo }
              : s.formData.details,
          },
          history: s.history,
          hasStartedFlow: s.hasStartedFlow,
          hasEntered: s.hasEntered,
          savedAt: s.savedAt,
          isComplete: s.isComplete,
          submissionId: s.submissionId,
          completedAt: s.completedAt,
          awaitingCap: s.awaitingCap,
          capName: s.capName,
          capEmail: s.capEmail,
          liteMode: s.liteMode,
        }
      },
    },
  ),
)
